'use client';

import { c as _c } from "react/compiler-runtime";
import { MapPin, Mail, Github, Linkedin, Download, Check, GraduationCap, Trophy, Languages, Copy, Users, BadgeCheck, ArrowUpRight } from 'lucide-react';
import { Mode, ModeConfig } from '@/components/shared/types';
import { getBorderBgColor } from '@/components/shared/helpers';
import SkillsSection from './SkillsSection';
import { useTranslation } from '@/context/I18nContext';
import LanguageSwitcher from '@/components/shared/LanguageSwitcher';
import { getProfileData, getEducationData, getCompetitionsData, getCertificationsData } from '@/data/loader';
import { trackEvent } from '@/lib/umami';
interface ProfileSectionProps {
  activeMode: Mode;
  design: ModeConfig['design'];
  isDark: boolean;
  copied: boolean;
  onCopyEmail: () => void;
  skills: string[];
}
export default function ProfileSection(t0) {
  const $ = _c(176);
  const {
    activeMode,
    design,
    isDark,
    copied,
    onCopyEmail,
    skills
  } = t0;
  const {
    t,
    locale
  } = useTranslation();
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== activeMode || $[1] !== copied || $[2] !== design || $[3] !== isDark || $[4] !== locale || $[5] !== onCopyEmail || $[6] !== skills || $[7] !== t) {
    const profileData = getProfileData(locale);
    const eduData = getEducationData(locale);
    const compData = getCompetitionsData(locale);
    const certData = getCertificationsData();
    const scrollbarClass = `scrollbar-perspective-${activeMode}`;
    const qaGlass = `${design.cardBg} ${design.cardBorder} ${design.shadow}`;
    const asideCardClass = `${activeMode === "ai" ? "p-6" : "p-5"} ${design.borderRadius === "rounded-xl" ? "rounded-xl" : design.borderRadius === "rounded-2xl" ? "rounded-2xl" : design.borderRadius === "rounded-3xl" ? "rounded-3xl" : "rounded-lg"} border transition-all duration-500 ${design.shadow} ${activeMode === "mobile" ? "glass-effect border-slate-400/20" : activeMode === "ai" ? `${design.cardBg} ${design.cardBorder}` : isDark ? `bg-white/5 ${design.border}` : activeMode === "frontend" ? `bg-white ${design.border}` : activeMode === "qa" ? qaGlass : `bg-white ${design.border}`}`;
    const qaSectionWrapper = activeMode === "qa" ? `border ${qaGlass} p-4 rounded-lg` : "";
    const qaHeaderWrapper = activeMode === "qa" ? `border ${qaGlass} p-4 rounded-lg` : "";
    const mobileLangWidth = activeMode === "qa" ? "w-full" : "w-full max-w-[240px]";
    let t15;
    if ($[22] !== activeMode) {
      t15 = activeMode === "backend" && <span className="terminal-prompt" />;
      $[22] = activeMode;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    const identitySection = <div className={`space-y-4 ${qaHeaderWrapper}`}><h1 className={`text-5xl font-bold tracking-tight sm:text-6xl ${design.textPrimary} ${activeMode === "blockchain" ? "green-glow" : ""}`}>{t15}{profileData.name}</h1><p className={`text-2xl font-medium ${design.accent}`}>{profileData.title}</p><p className={`max-w-md text-base leading-relaxed ${design.textSecondary}`}>{profileData.tagline}</p></div>;
    t12 = "profile-section";
    t13 = `lg:col-span-5 lg:sticky lg:top-16 lg:self-start ${"lg:h-auto lg:overflow-visible"} ${scrollbarClass} flex flex-col gap-10 ${activeMode === "ai" ? "p-6" : "p-4"}`;
    t14 = identitySection;
    t10 = "grid grid-cols-1 sm:grid-cols-2 gap-8 items-start mx-2";
    const t16 = `space-y-8 ${design.textSecondary}`;
    const t17 = `flex flex-col gap-3 text-sm ${qaSectionWrapper}`;
    let t18;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <MapPin className="h-4 w-4 opacity-70" />;
      $[24] = t18;
    } else {
      t18 = $[24];
    }
    const t19 = <div className="flex items-center gap-2">{t18}<span>{profileData.location}</span></div>;
    let t20;
    if ($[25] !== copied) {
      t20 = copied ? <Check className="h-4 w-4 text-green-500" /> : <Mail className="h-4 w-4 opacity-70" />;
      $[25] = copied;
      $[26] = t20;
    } else {
      t20 = $[26];
    }
    let t21;
    if ($[27] !== copied) {
      t21 = !copied && <Copy className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />;
      $[27] = copied;
      $[28] = t21;
    } else {
      t21 = $[28];
    }
    const t22 = <button onClick={onCopyEmail} className="flex items-center gap-2 hover:text-current transition group text-left w-fit">{t20}<span className={copied ? "text-green-500" : ""}>{copied ? t("common.emailCopied") : profileData.contacts.email}</span>{t21}</button>;
    const t23 = profileData.contacts.github;
    let t24;
    if ($[29] !== t) {
      t24 = t("accessibility.openGithubProfile");
      $[29] = t;
      $[30] = t24;
    } else {
      t24 = $[30];
    }
    let t25;
    if ($[31] !== activeMode || $[32] !== locale) {
      t25 = () => trackEvent("social_click", {
        platform: "github",
        mode: activeMode,
        locale
      });
      $[31] = activeMode;
      $[32] = locale;
      $[33] = t25;
    } else {
      t25 = $[33];
    }
    let t26;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <Github className="h-5 w-5" aria-hidden="true" />;
      $[34] = t26;
    } else {
      t26 = $[34];
    }
    const t27 = profileData.contacts.linkedin;
    let t28;
    if ($[35] !== t) {
      t28 = t("accessibility.openLinkedinProfile");
      $[35] = t;
      $[36] = t28;
    } else {
      t28 = $[36];
    }
    let t29;
    if ($[37] !== activeMode || $[38] !== locale) {
      t29 = () => trackEvent("social_click", {
        platform: "linkedin",
        mode: activeMode,
        locale
      });
      $[37] = activeMode;
      $[38] = locale;
      $[39] = t29;
    } else {
      t29 = $[39];
    }
    let t30;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <Linkedin className="h-5 w-5" aria-hidden="true" />;
      $[40] = t30;
    } else {
      t30 = $[40];
    }
    const t31 = <div className="flex gap-4 mt-2"><a href={t23} target="_blank" rel="noopener noreferrer" className="hover:text-current transition" aria-label={t24} onClick={t25}>{t26}</a><a href={t27} target="_blank" rel="noopener noreferrer" className="hover:text-current transition" aria-label={t28} onClick={t29}>{t30}</a></div>;
    let t32;
    if ($[41] !== activeMode || $[42] !== locale) {
      t32 = () => trackEvent("cv_download", {
        file: "gafur-apil-cv.pdf",
        mode: activeMode,
        locale
      });
      $[41] = activeMode;
      $[42] = locale;
      $[43] = t32;
    } else {
      t32 = $[43];
    }
    const t33 = `mt-4 flex w-full sm:w-auto max-w-[240px] items-center justify-center gap-2 ${design.borderRadius} px-4 py-2 text-xs font-bold transition-all ${design.shadow} ${isDark ? "bg-white/10 text-white hover:bg-white/20" : activeMode === "frontend" ? "bg-rose-100 text-rose-800 hover:bg-rose-200" : activeMode === "qa" ? "bg-cyan-100 text-cyan-800 hover:bg-cyan-200" : "bg-slate-200 text-slate-800 hover:bg-slate-300"}`;
    let t34;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <Download className="h-3 w-3" />;
      $[44] = t34;
    } else {
      t34 = $[44];
    }
    let t35;
    if ($[45] !== t) {
      t35 = t("common.downloadCV");
      $[45] = t;
      $[46] = t35;
    } else {
      t35 = $[46];
    }
    let t36;
    if ($[47] !== t35) {
      t36 = <span>{t35}</span>;
      $[47] = t35;
      $[48] = t36;
    } else {
      t36 = $[48];
    }
    let t37;
    if ($[49] !== t32 || $[50] !== t33 || $[51] !== t36) {
      t37 = <a href="/gafur-apil-cv.pdf" download={true} onClick={t32} className={t33}>{t34}{t36}</a>;
      $[49] = t32;
      $[50] = t33;
      $[51] = t36;
      $[52] = t37;
    } else {
      t37 = $[52];
    }
    let t38;
    if ($[53] !== t17 || $[54] !== t19 || $[55] !== t22 || $[56] !== t31 || $[57] !== t37) {
      t38 = <div className={t17} data-sublayer-id="profile-contact" data-sublayer-name="Contact">{t19}{t22}{t31}{t37}</div>;
      $[53] = t17;
      $[54] = t19;
      $[55] = t22;
      $[56] = t31;
      $[57] = t37;
      $[58] = t38;
    } else {
      t38 = $[58];
    }
    const t39 = `sticky top-3 z-20 sm:hidden ${mobileLangWidth}`;
    let t40;
    if ($[59] !== design || $[60] !== isDark) {
      t40 = <LanguageSwitcher design={design} isDark={isDark} />;
      $[59] = design;
      $[60] = isDark;
      $[61] = t40;
    } else {
      t40 = $[61];
    }
    let t41;
    if ($[62] !== t39 || $[63] !== t40) {
      t41 = <div className={t39}>{t40}</div>;
      $[62] = t39;
      $[63] = t40;
      $[64] = t41;
    } else {
      t41 = $[64];
    }
    let t42;
    if ($[65] !== activeMode) {
      t42 = getBorderBgColor(activeMode);
      $[65] = activeMode;
      $[66] = t42;
    } else {
      t42 = $[66];
    }
    const t43 = `w-full h-px ${t42} opacity-30`;
    let t44;
    if ($[67] !== t43) {
      t44 = <div className={t43} />;
      $[67] = t43;
      $[68] = t44;
    } else {
      t44 = $[68];
    }
    let t45;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t45 = <GraduationCap className="h-4 w-4" />;
      $[69] = t45;
    } else {
      t45 = $[69];
    }
    let t46;
    if ($[70] !== t) {
      t46 = t("common.education");
      $[70] = t;
      $[71] = t46;
    } else {
      t46 = $[71];
    }
    let t47;
    if ($[72] !== t46) {
      t47 = <h3 className="text-xs font-bold uppercase tracking-widest mb-4 opacity-50 flex items-center gap-2">{t45}<span>{t46}</span></h3>;
      $[72] = t46;
      $[73] = t47;
    } else {
      t47 = $[73];
    }
    let t48;
    if ($[74] !== activeMode || $[75] !== design.textPrimary || $[76] !== design.textSecondary) {
      t48 = (edu, i) => <div key={i} className="mb-4 text-sm"><div className={`font-semibold ${design.textPrimary}`}>{activeMode === "qa" && <Check className="inline h-3 w-3 mr-2 text-green-500" />}{edu.school}</div><div className={design.textSecondary}>{edu.degree}</div><div className={`text-xs opacity-60 mt-1 ${design.textSecondary}`}>{edu.year}</div></div>;
      $[74] = activeMode;
      $[75] = design.textPrimary;
      $[76] = design.textSecondary;
      $[77] = t48;
    } else {
      t48 = $[77];
    }
    const t49 = eduData.schools.map(t48);
    const t50 = eduData.communities && eduData.communities.length > 0 && <><h3 className="text-xs font-bold uppercase tracking-widest mb-4 mt-6 opacity-50 flex items-center gap-2"><Users className="h-4 w-4" /><span>{t("common.communities")}</span></h3>{eduData.communities.map((comm, i_0) => <div key={i_0} className="mb-4 text-sm"><div className={`font-semibold ${design.textPrimary}`}>{activeMode === "qa" && <Check className="inline h-3 w-3 mr-2 text-green-500" />}{comm.name}</div><div className={`${design.accent} text-xs font-medium`}>{comm.role}</div></div>)}</>;
    let t51;
    if ($[78] !== qaSectionWrapper || $[79] !== t47 || $[80] !== t49 || $[81] !== t50) {
      t51 = <div data-sublayer-id="profile-education" data-sublayer-name="Education" className={qaSectionWrapper}>{t47}{t49}{t50}</div>;
      $[78] = qaSectionWrapper;
      $[79] = t47;
      $[80] = t49;
      $[81] = t50;
      $[82] = t51;
    } else {
      t51 = $[82];
    }
    let t52;
    if ($[83] !== activeMode) {
      t52 = getBorderBgColor(activeMode);
      $[83] = activeMode;
      $[84] = t52;
    } else {
      t52 = $[84];
    }
    const t53 = `w-full h-px ${t52} opacity-30`;
    let t54;
    if ($[85] !== t53) {
      t54 = <div className={t53} />;
      $[85] = t53;
      $[86] = t54;
    } else {
      t54 = $[86];
    }
    let t55;
    if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
      t55 = <Trophy className="h-4 w-4" />;
      $[87] = t55;
    } else {
      t55 = $[87];
    }
    let t56;
    if ($[88] !== t) {
      t56 = t("common.competitions");
      $[88] = t;
      $[89] = t56;
    } else {
      t56 = $[89];
    }
    let t57;
    if ($[90] !== t56) {
      t57 = <h3 className="text-xs font-bold uppercase tracking-widest mb-4 opacity-50 flex items-center gap-2">{t55}<span>{t56}</span></h3>;
      $[90] = t56;
      $[91] = t57;
    } else {
      t57 = $[91];
    }
    let t58;
    if ($[92] !== activeMode || $[93] !== design.accent || $[94] !== design.textPrimary || $[95] !== design.textSecondary) {
      t58 = (comp, i_1) => <div key={i_1} className="mb-4 text-sm"><div className={`font-semibold ${design.textPrimary}`}>{activeMode === "qa" && <Check className="inline h-3 w-3 mr-2 text-green-500" />}{comp.title}</div><div className={`${design.accent} text-xs font-medium`}>{comp.rank}</div><div className={design.textSecondary}>{comp.project}</div><div className={`text-xs opacity-60 mt-1 ${design.textSecondary}`}>{comp.year}</div>{comp.desc && <div className={`text-xs mt-1 opacity-70 ${design.textSecondary}`}>{comp.desc}</div>}</div>;
      $[92] = activeMode;
      $[93] = design.accent;
      $[94] = design.textPrimary;
      $[95] = design.textSecondary;
      $[96] = t58;
    } else {
      t58 = $[96];
    }
    const t59 = compData.map(t58);
    let t60;
    if ($[97] !== qaSectionWrapper || $[98] !== t57 || $[99] !== t59) {
      t60 = <div data-sublayer-id="profile-competitions" data-sublayer-name="Competitions" className={qaSectionWrapper}>{t57}{t59}</div>;
      $[97] = qaSectionWrapper;
      $[98] = t57;
      $[99] = t59;
      $[100] = t60;
    } else {
      t60 = $[100];
    }
    let t61;
    if ($[101] !== activeMode) {
      t61 = getBorderBgColor(activeMode);
      $[101] = activeMode;
      $[102] = t61;
    } else {
      t61 = $[102];
    }
    const t62 = `w-full h-px ${t61} opacity-30`;
    let t63;
    if ($[103] !== t62) {
      t63 = <div className={t62} />;
      $[103] = t62;
      $[104] = t63;
    } else {
      t63 = $[104];
    }
    let t64;
    if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
      t64 = <Languages className="h-4 w-4" />;
      $[105] = t64;
    } else {
      t64 = $[105];
    }
    let t65;
    if ($[106] !== t) {
      t65 = t("common.languages");
      $[106] = t;
      $[107] = t65;
    } else {
      t65 = $[107];
    }
    let t66;
    if ($[108] !== t65) {
      t66 = <h3 className="text-xs font-bold uppercase tracking-widest mb-4 opacity-50 flex items-center gap-2">{t64}<span>{t65}</span></h3>;
      $[108] = t65;
      $[109] = t66;
    } else {
      t66 = $[109];
    }
    let t67;
    if ($[110] !== activeMode || $[111] !== design.borderRadius || $[112] !== isDark) {
      t67 = (l, i_2) => <span key={i_2} className={`text-xs px-2 py-1 ${design.borderRadius} border ${isDark ? "border-white/10 bg-white/5" : activeMode === "frontend" ? "bg-transparent" : activeMode === "qa" ? "border-white/40 bg-white/40 backdrop-blur text-cyan-800 shadow-sm shadow-cyan-100/40" : "border-slate-200 bg-slate-50"}`} style={activeMode === "frontend" ? {
        borderColor: "color-mix(in srgb, var(--accent, #E11D48) 45%, transparent)",
        backgroundColor: "color-mix(in srgb, var(--accent, #E11D48) 12%, transparent)",
        color: "var(--accent, #E11D48)"
      } : undefined}>{l.lang} <span className="opacity-50">({l.level})</span></span>;
      $[110] = activeMode;
      $[111] = design.borderRadius;
      $[112] = isDark;
      $[113] = t67;
    } else {
      t67 = $[113];
    }
    const t68 = profileData.languages.map(t67);
    let t69;
    if ($[114] !== t68) {
      t69 = <div className="flex flex-wrap gap-2">{t68}</div>;
      $[114] = t68;
      $[115] = t69;
    } else {
      t69 = $[115];
    }
    let t70;
    if ($[116] !== qaSectionWrapper || $[117] !== t66 || $[118] !== t69) {
      t70 = <div className={qaSectionWrapper}>{t66}{t69}</div>;
      $[116] = qaSectionWrapper;
      $[117] = t66;
      $[118] = t69;
      $[119] = t70;
    } else {
      t70 = $[119];
    }
    if ($[120] !== t16 || $[121] !== t38 || $[122] !== t41 || $[123] !== t44 || $[124] !== t51 || $[125] !== t54 || $[126] !== t60 || $[127] !== t63 || $[128] !== t70) {
      t11 = <div className={t16}>{t38}{t41}{t44}{t51}{t54}{t60}{t63}{t70}</div>;
      $[120] = t16;
      $[121] = t38;
      $[122] = t41;
      $[123] = t44;
      $[124] = t51;
      $[125] = t54;
      $[126] = t60;
      $[127] = t63;
      $[128] = t70;
      $[129] = t11;
    } else {
      t11 = $[129];
    }
    t7 = "flex flex-col gap-6";
    if ($[130] !== design || $[131] !== isDark) {
      t8 = <div className="hidden sm:block"><LanguageSwitcher design={design} isDark={isDark} /></div>;
      $[130] = design;
      $[131] = isDark;
      $[132] = t8;
    } else {
      t8 = $[132];
    }
    if ($[133] !== activeMode || $[134] !== design || $[135] !== isDark || $[136] !== skills) {
      t9 = <div data-sublayer-id="profile-skills" data-sublayer-name="Skills"><SkillsSection activeMode={activeMode} design={design} isDark={isDark} skills={skills} /></div>;
      $[133] = activeMode;
      $[134] = design;
      $[135] = isDark;
      $[136] = skills;
      $[137] = t9;
    } else {
      t9 = $[137];
    }
    t3 = "profile-certifications";
    t4 = "Certifications";
    t5 = asideCardClass;
    const t71 = `flex items-center gap-2 mb-4 text-xs font-bold uppercase tracking-widest opacity-50 ${design.textSecondary}`;
    let t72;
    if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
      t72 = <BadgeCheck className="h-3.5 w-3.5" />;
      $[138] = t72;
    } else {
      t72 = $[138];
    }
    let t73;
    if ($[139] !== t) {
      t73 = t("common.certifications");
      $[139] = t;
      $[140] = t73;
    } else {
      t73 = $[140];
    }
    let t74;
    if ($[141] !== t73) {
      t74 = <span>{t73}</span>;
      $[141] = t73;
      $[142] = t74;
    } else {
      t74 = $[142];
    }
    if ($[143] !== t71 || $[144] !== t74) {
      t6 = <div className={t71}>{t72}{t74}</div>;
      $[143] = t71;
      $[144] = t74;
      $[145] = t6;
    } else {
      t6 = $[145];
    }
    t1 = "space-y-3";
    let t75;
    if ($[146] !== activeMode || $[147] !== design.accent || $[148] !== design.textPrimary || $[149] !== design.textSecondary || $[150] !== locale || $[151] !== t) {
      t75 = cert => <div key={cert.id} className="flex items-start justify-between gap-3"><div className="flex-1"><div className={`text-sm font-semibold ${design.textPrimary}`}>{activeMode === "qa" && <Check className="inline h-3 w-3 mr-2 text-green-500" />}{cert.title}</div><div className={`text-xs ${design.textSecondary}`}>{cert.issuer}</div></div><div className="flex flex-col items-end gap-1 text-xs min-w-[84px]"><span className={`${design.textSecondary} opacity-70`}>{cert.year}</span>{cert.link && <a href={cert.link} target="_blank" rel="noopener noreferrer" className={`${design.accent} hover:opacity-80 transition flex items-center justify-center`} aria-label={t("accessibility.openCertificate", {
            title: cert.title
          })} onClick={() => trackEvent("cert_view", {
            cert_id: cert.id,
            cert_title: cert.title,
            mode: activeMode,
            locale
          })}><ArrowUpRight className="h-4 w-4" aria-hidden="true" /></a>}</div></div>;
      $[146] = activeMode;
      $[147] = design.accent;
      $[148] = design.textPrimary;
      $[149] = design.textSecondary;
      $[150] = locale;
      $[151] = t;
      $[152] = t75;
    } else {
      t75 = $[152];
    }
    t2 = certData.map(t75);
    $[0] = activeMode;
    $[1] = copied;
    $[2] = design;
    $[3] = isDark;
    $[4] = locale;
    $[5] = onCopyEmail;
    $[6] = skills;
    $[7] = t;
    $[8] = t1;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t1 = $[8];
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t14 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t15;
  if ($[153] !== t1 || $[154] !== t2) {
    t15 = <div className={t1}>{t2}</div>;
    $[153] = t1;
    $[154] = t2;
    $[155] = t15;
  } else {
    t15 = $[155];
  }
  let t16;
  if ($[156] !== t15 || $[157] !== t3 || $[158] !== t4 || $[159] !== t5 || $[160] !== t6) {
    t16 = <div data-sublayer-id={t3} data-sublayer-name={t4} className={t5}>{t6}{t15}</div>;
    $[156] = t15;
    $[157] = t3;
    $[158] = t4;
    $[159] = t5;
    $[160] = t6;
    $[161] = t16;
  } else {
    t16 = $[161];
  }
  let t17;
  if ($[162] !== t16 || $[163] !== t7 || $[164] !== t8 || $[165] !== t9) {
    t17 = <div className={t7}>{t8}{t9}{t16}</div>;
    $[162] = t16;
    $[163] = t7;
    $[164] = t8;
    $[165] = t9;
    $[166] = t17;
  } else {
    t17 = $[166];
  }
  let t18;
  if ($[167] !== t10 || $[168] !== t11 || $[169] !== t17) {
    t18 = <div className={t10}>{t11}{t17}</div>;
    $[167] = t10;
    $[168] = t11;
    $[169] = t17;
    $[170] = t18;
  } else {
    t18 = $[170];
  }
  let t19;
  if ($[171] !== t12 || $[172] !== t13 || $[173] !== t14 || $[174] !== t18) {
    t19 = <><header data-section-id={t12} className={t13}>{t14}{t18}</header></>;
    $[171] = t12;
    $[172] = t13;
    $[173] = t14;
    $[174] = t18;
    $[175] = t19;
  } else {
    t19 = $[175];
  }
  return t19;
}